import React, { useEffect, useState } from "react";
import { pickPortfolio, PF_EVENT } from "./selectedPortfolio";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Building2, FileBarChart, LogOut, Loader2, ExternalLink, ShieldCheck, GitCompare, X, Users, School, CalendarRange } from "lucide-react";
import { API } from "./shared";
import { PortalTopBar } from "@/components/PortalTopBar";
import PortfolioDashboard from "./PortfolioDashboard";
import PortfolioTeam from "./PortfolioTeam";
import SchoolsManagement from "./SchoolsManagement";
import DiagnosticPeriods from "./DiagnosticPeriods";
import PeriodComparison from "./PeriodComparison";
import SchoolDrillDown from "./SchoolDrillDown";
import BenchmarkAnalysis from "./BenchmarkAnalysis";
import SchoolOnboardingWizard from "./SchoolOnboardingWizard";
import VendorPO from "./VendorPO";
import ActivationCenter from "./ActivationCenter";
import SchoolDiagnosticView from "./SchoolDiagnosticView";
import SchoolCompare from "./SchoolCompare";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Google login removed — unified auth uses the standard /diagnostic/login screen.

const PORTFOLIO_TYPE_LABEL = {
  authorizer_portfolio: "Authorizer",
  cmo_emo: "EMO/CMO",
  charter_network: "Charter Network",
  district_regional_operator: "District / Regional",
  consultant_advisor: "Consultant",
  "589_labs_internal": "589 Labs",
  other: "Portfolio",
};

const NAV = [
  { to: "/portfolio/dashboard", label: "Portfolio Dashboard", icon: LayoutDashboard, testid: "nav-portfolio-dashboard" },
  { to: "/portfolio/schools", label: "Schools", icon: School, testid: "nav-portfolio-schools" },
  { to: "/portfolio/periods", label: "Diagnostic Periods", icon: CalendarRange, testid: "nav-portfolio-periods" },
  { to: "/portfolio/team", label: "Users", icon: Users, testid: "nav-portfolio-team" },
  { to: "/portfolio/onboarding", label: "School Onboarding", icon: Building2, testid: "nav-school-onboarding" },
  { to: "/portfolio/vendor-po", label: "Vendor & PO", icon: FileBarChart, testid: "nav-vendor-po" },
];

export default function PortfolioOwnerArea() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("checking"); // checking | ok | nologin | denied
  const [user, setUser] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pfId, setPfId] = useState(null);
  useEffect(() => {
    const on = (e) => setPfId(e.detail);
    window.addEventListener(PF_EVENT, on);
    return () => window.removeEventListener(PF_EVENT, on);
  }, []);

  const isOAuth = location.hash?.includes("session_id=");
  const currentPf = portfolios.find((p) => p.id === pfId) || portfolios[0];

  useEffect(() => {
    (async () => {
      // Handle OAuth return: exchange session_id first.
      if (isOAuth) {
        const params = new URLSearchParams(location.hash.replace("#", "?"));
        const sid = params.get("session_id");
        if (sid) {
          try {
            await fetch(`${API}/api/auth/session`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              credentials: "include", body: JSON.stringify({ session_id: sid }),
            });
          } catch {}
          window.history.replaceState(null, "", "/portfolio/dashboard");
        }
      }
      try {
        const r = await fetch(`${API}/api/portfolio/me`, { credentials: "include" });
        if (r.status === 401) { setState("nologin"); return; }
        const d = await r.json();
        setUser(d.user);
        setPortfolios(d.portfolios || []);
        setPfId(pickPortfolio(d.portfolios)?.id || null);
        if (d.is_portfolio_owner || d.is_admin) setState("ok");
        else setState("denied");
      } catch {
        setState("nologin");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch {}
    try { localStorage.removeItem("diag_session"); sessionStorage.clear(); } catch {}
    navigate("/diagnostic/login", { replace: true });
  };

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  if (state === "checking") {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  if (state === "nologin") {
    // Unified auth: portfolio owners sign in through the standard School Health
    // Diagnostic workspace login (which routes portfolio_owner → dashboard).
    return <Navigate to="/diagnostic/login" replace />;
  }

  if (state === "denied") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <Building2 className="mx-auto h-10 w-10 text-slate-300" />
          <h1 className="mt-4 text-xl font-semibold text-slate-900">No Portfolio Access</h1>
          <p className="mt-2 text-sm text-slate-500">Your account ({user?.email}) is not assigned to any portfolio. Please contact your 589 Labs administrator.</p>
          <button onClick={signOut} className="mt-6 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">Sign out</button>
        </div>
      </div>
    );
  }

  // Founding Partners and Demo Environments don't self-serve payment/onboarding —
  // hide "School Onboarding" and "Vendor & PO" to reduce confusion.
  const hideSetup = user?.is_pilot || user?.founding_partner || user?.is_demo;
  const navItems = NAV.filter((n) =>
    !(hideSetup && (n.to === "/portfolio/vendor-po" || n.to === "/portfolio/onboarding")));

  // Portfolio renders its own "Return to Admin" control in the top bar, so no
  // fixed banner overlays the layout and no offset padding is needed.
  const bannerPad = 0;

  return (
    <div className="min-h-screen bg-[#f9fafb] text-[#111827] portfolio-touch" data-portfolio-area>
      {/* Desktop Sidebar */}
      <aside aria-label="Workspace" style={{ top: bannerPad }} className="fixed bottom-0 left-0 hidden w-60 flex-col border-r border-[#e5e7eb] bg-white lg:flex">
        <div className="flex h-16 items-center gap-2 border-b border-[#e5e7eb] px-5">
          <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="w-[156px] h-auto max-w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        <nav aria-label="Main" className="flex-1 space-y-1 px-3 py-4">
          {navItems.map((n, i) => n.disabled ? (
            <div key={i} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300">
              <n.icon className="h-4 w-4" /> {n.label} <span className="ml-auto text-[10px] uppercase">Soon</span>
            </div>
          ) : (
            <NavLink key={i} to={n.to} end data-testid={n.testid}
              className={({ isActive }) => `relative flex items-center gap-2.5 rounded-md px-3 min-h-[44px] text-[15px] font-semibold ${isActive ? "bg-[#e6f4f8] text-[#00698a] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:bg-[#007a9e] before:rounded-r" : "text-[#374151] hover:bg-[#f3f4f6]"}`}>
              <n.icon className="h-[18px] w-[18px]" /> {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="portfolio-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <aside aria-label="Workspace" style={{ top: bannerPad }} className="absolute bottom-0 left-0 flex w-64 max-w-[80%] flex-col border-r border-[#e5e7eb] bg-white shadow-xl">
            <div className="flex h-16 items-center justify-between border-b border-[#e5e7eb] px-4">
              <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="w-[156px] h-auto max-w-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <button onClick={() => setMenuOpen(false)} data-testid="portfolio-drawer-close" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <nav aria-label="Main" className="flex-1 space-y-1 px-3 py-4">
              {navItems.map((n, i) => n.disabled ? null : (
                <NavLink key={i} to={n.to} end data-testid={`m-${n.testid}`}
                  className={({ isActive }) => `relative flex items-center gap-2.5 rounded-md px-3 min-h-[44px] text-[15px] font-semibold ${isActive ? "bg-[#e6f4f8] text-[#00698a] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:bg-[#007a9e] before:rounded-r" : "text-[#374151] hover:bg-[#f3f4f6]"}`}>
                  <n.icon className="h-[18px] w-[18px]" /> {n.label}
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      <div className="lg:pl-60" style={{ paddingTop: bannerPad }}>
        <PortalTopBar
          label={PORTFOLIO_TYPE_LABEL[currentPf?.type] || "Portfolio"}
          orgName={currentPf?.name || "Portfolio"}
          accent="#007a9e"
          onSignOut={signOut}
          onHamburger={() => setMenuOpen(true)}
          shareEnabled
          feedbackPortalType="Portfolio Manager"
        />
        {/* Read-only banner */}
        <div data-testid="portfolio-readonly-banner" role="note" className="flex items-center justify-between gap-2 bg-[#e6f4f8] px-6 py-2 text-[13.5px] font-semibold text-[#00698a]">
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#00698a]" />
            {["admin", "super_admin"].includes(user?.role)
              ? "Admin Preview — read-only portfolio owner view"
              : "Portfolio view · school diagnostics are view-only · your assessments are private to your Portfolio"}
          </span>
          <span className="flex items-center gap-3">
            {["admin", "super_admin"].includes(user?.role) && (
              <a href="/app/portfolios" data-testid="portfolio-back-to-admin" className="text-[#00607d] hover:underline">← Back to Admin</a>
            )}
            <span className="hidden sm:inline text-[#1f2937] font-normal">{user?.email}</span>
          </span>
        </div>
        <main id="main" className="px-5 py-6 sm:px-8">
          {(location.pathname.includes("/portfolio/onboarding") || location.pathname.includes("/portfolio/activation")) && (
            <div className="flex gap-1 mb-5 border-b border-[#e5e7eb]" data-testid="onboarding-subnav">
              <NavLink to="/portfolio/onboarding" end className={({ isActive }) => `px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px ${isActive ? "border-[#007a9e] text-[#111827]" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid="subnav-wizard">Onboarding Wizard</NavLink>
              <NavLink to="/portfolio/activation" end className={({ isActive }) => `px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px ${isActive ? "border-[#007a9e] text-[#111827]" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid="subnav-activation">Activate Schools</NavLink>
            </div>
          )}
          <Routes>
            <Route path="dashboard" element={<PortfolioDashboard />} />
            <Route path="team" element={<PortfolioTeam />} />
            <Route path="schools" element={<SchoolsManagement />} />
            <Route path="periods" element={<DiagnosticPeriods />} />
            <Route path="compare/:periodId" element={<PeriodComparison />} />
            <Route path="onboarding" element={<SchoolOnboardingWizard user={user} />} />
            <Route path="activation" element={<ActivationCenter />} />
            <Route path="vendor-po" element={<VendorPO />} />
            <Route path="schools/:tenantId/diagnostic" element={<SchoolDiagnosticView />} />
            <Route path="compare/:periodId/:tenantId" element={<SchoolCompare />} />
            <Route path="schools/:tenantId" element={<SchoolDrillDown />} />
            <Route path="benchmark" element={<BenchmarkAnalysis />} />
            <Route path="*" element={<Navigate to="/portfolio/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
